import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "components/Input";
import Button from "components/Button";
import ModalResetPasswordSuccessfully from "screens/ModalResetPasswordSuccessfully";
import { useMutation } from "@apollo/client";
import { COMPLETE_STAFF_SETUP, CONFIRM_STAFF_SETUP_EMAIL } from "graphql/service";
import { Container, ContentContainer } from "./styles";
import Logo from "assets/Logo.png";
import { HiddenIcon, VisibleIcon } from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";
import { message } from "antd";

const SetupAccount = ({ redirectPath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false); 
  const [successIsOpen, setSuccessIsOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);

  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [confirmStaffSetupEmail, {data: confirmStaffSetupEmailData, loading: confirmStaffSetupEmailLoading }] = useMutation(CONFIRM_STAFF_SETUP_EMAIL, {
    onCompleted: (confirmStaffSetupEmailData) => {
      if (confirmStaffSetupEmailData.confirmStaffSetupEmail?.sessionToken) {
        setEmailVerified(true);
      }
    },
    onError: error => {
      if (!error?.networkError) {
        let content = error.message;
        if (error.message.includes('Validation')) {
          content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const [resendStaffSetupPhoneCode] = useMutation(CONFIRM_STAFF_SETUP_EMAIL, {
    onCompleted: (data) => {
      message.success("Verification code resent successfully");
    },
    onError: error => {
      if (!error?.networkError) {
        let content = error.message;
        if (error.message.includes('Validation')) {
          content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const sendPhoneVerificationCode = () => {
    confirmStaffSetupEmail({
      variables: {
        input: {
          token
        },
      },
    });
  };

  const resendPhoneVerificationCode = () => {
    resendStaffSetupPhoneCode({
      variables: {
        input: {
          token
        },
      },
    });
  };

  const [completeStaffSetup, { loading: resetPasswordLoading }] = useMutation(COMPLETE_STAFF_SETUP, {
    onCompleted: (setupData) => {
      if (setupData.completeStaffSetup?.user?.userId) {
        setSuccessIsOpen(true);
      } else {
        const content = "Set password failed!";
        debouncedErrorMessage(content);
        setSuccessIsOpen(false);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const handleContinue = () => {
    if (password.length >= 8) {
      completeStaffSetup({
        variables: {
          input: {
            token,
            newPassword: password,
            //TODO: The following fields is not required by backend but potentially needed in the future, so keep them here for now
            // phoneVerificationCode: 
          },
        },
      });
    } else {
      setPasswordError(true);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Setup - Rook Hotel PMS</title>
        <meta name="description" content="Setup - Rook Hotel PMS" />
      </Helmet>
      <ContentContainer>
        <img src={Logo} alt="Logo" />
        <div className="brand">
          <span className="rook">ROOK </span>
          <span className="hotel">HOTEL</span>
        </div>
        {phoneVerified ? (
          <div className="infoContainer">
            <div className="title">Setup Password</div>
            <div className="setupPassword">
              <Input
                title={"NEW PASSWORD"}
                required
                error={passwordError}
                errorMessage={
                  password.length
                    ? password.length < 8 ? "Password must contain at least 8 characters.": ""
                    : "This field is required."
                }
                placeholder={"Password"}
                readOnly
                onFocus={(e) => e.target.removeAttribute('readOnly')}
                onChange={(e) => setPassword(e.target.value)}
                type={passwordHidden ? "password" : "text"}
                additionalstyle={`
                  width: 100%;
                  margin-top: 24px;
                  input {
                    font-size: 14px;
                    font-weight: 400;
                  }
                `}
              />
              {passwordHidden ? (
                <div
                  className="passwordLock"
                  onClick={() => {
                    setPasswordHidden(false);
                  }}
                >
                  <HiddenIcon />
                </div>
              ) : (
                <div
                  className="passwordUnlock"
                  onClick={() => {
                    setPasswordHidden(true);
                  }}
                >
                  <VisibleIcon />
                </div>
              )}
            </div>
            <Button
              mainPage
              onClick={handleContinue}
              data-loading={resetPasswordLoading}
              additionalstyle={`
                width: 97px;
                margin-top: 24px;
              `}
            >
              {resetPasswordLoading ? null : "Continue"}
            </Button>
          </div>
        ): emailVerified ? (
          <div className="infoContainer">
            <div className="title">Verify Your Phone</div>
            <div className="description">
              {`We’ve sent a 6‑digit code to: ${confirmStaffSetupEmailData.confirmStaffSetupEmail?.maskedPhone}.`}
            </div>
          </div>
        ): token ? (
          <div className="infoContainer">
            <div className="title">Your email has been verified!</div>
            <div className="description">
              To finish setting up your account, we need to verify your phone number.
            </div>
            <Button
              mainPage
              onClick={sendPhoneVerificationCode}
              data-loading={confirmStaffSetupEmailLoading}
              additionalstyle={`
                width: 97px;
                margin-top: 24px;
              `}
            >
              {confirmStaffSetupEmailLoading ? null : "Continue"}
            </Button>
          </div>
        ):(
          <div className="infoContainer">
            <div className="title">Account Not Found</div>
          </div>
        )}
      </ContentContainer>
      <ModalResetPasswordSuccessfully
        isOpen={successIsOpen}
        setClose={() => {
          setSuccessIsOpen(false);
          navigate('/login');
        }}
      />
    </Container>
  );
};

export default SetupAccount;
